import React, { useEffect, useMemo } from 'react';
import { loadPbrMaps, disposePbrMaps, ensureUv2, PBR_SETS, PAINT_SET_KEY, useTintProps } from './pbrTextures.jsx';
import { FloorEdgeAo, AoStrip, AO } from './ambientOcclusion.jsx';
import { buildSurfaceGeometry, spaceEdges, edgeBand, inward, piecesFrame } from './spacePieces.jsx';

// هر نوعِ کفِ «واقعی/PBR» به یکی از ستِ‌های pbrTextures.js وصل می‌شود — دقیقاً همان الگویی که
// Wall3D.jsx برای PBR_WALL_TYPE_TO_SET دارد؛ افزودنِ یک بافتِ واقعیِ تازه فقط یعنی یک ردیفِ تازه این‌جا
const PBR_FLOOR_TYPE_TO_SET = {
  ceramicPbr: 'ceramicTileWhite',
  concretePbr: 'concreteGray',
  parquetPbr: 'parquetOak',
  stoneSlabPbr: 'marbleVeined',
  stonePbr: 'graniteSpeckled',
  travertinePbr: 'travertine',
  carpetPbr: 'carpet',
  laminateLightPbr: 'laminateLight',
  laminateDarkPbr: 'laminateDark',
};

// «المان» کفِ سه‌بعدی: هر اتاق (roomGroupRect) کفِ جدای خودش را دارد و با تپ در حالتِ اوربیت
// انتخاب می‌شود تا پنلِ تنظیماتش (floorStyleBox.jsx) باز شود. جنس‌ها فقط دو دسته‌اند:
//   • رنگِ ساده (paint) — یک رنگِ یک‌دست
//   • عکس‌های واقعیِ PBR (pbrTextures.jsx) — رنگِ کاربر روی خودِ عکس «ضرب» می‌شود
// بافت‌های کدنویسی‌شده‌ی قبلی (پارکت/کاشی/سنگ/موکت/بتنِ canvas) حذف شده‌اند؛ کف‌های ذخیره‌شده با آن
// نوع‌ها در resolveFloorStyle خودکار به معادلِ واقعی‌شان تبدیل می‌شوند.
// اگر کاربر برای اتاقی چیزی تنظیم نکرده باشد، «رنگِ ساده» با رنگِ پیش‌فرضِ همان اتاق از پالتِ چرخشیِ
// Create3D.jsx (defaultColor) نشان داده می‌شود تا اتاق‌ها از هم قابل‌تشخیص بمانند

const DEFAULT_FLOOR_COLOR = '#e7dcc9';
const DEFAULT_FLOOR_ACCENT = '#c9bba0';

export const DEFAULT_FLOOR_STYLE = {
  type: 'paint',
  color: DEFAULT_FLOOR_COLOR,
  accent: DEFAULT_FLOOR_ACCENT,
  tileCm: 40,
  levelCm: 0,
};

// «اختلافِ سطح»: کفِ این اتاق چند سانت بالاتر (+) یا پایین‌تر (−) از سطحِ مبنای خانه است (مثلاً آشپزخانه
// +۱۰، حمام/بالکن −۵). محدوده عمداً ±۲۰ است — یک پله‌ی معمولی که بدونِ پله‌کانِ جدا می‌شود رد شد
export const FLOOR_LEVEL_MIN_CM = -20;
export const FLOOR_LEVEL_MAX_CM = 20;
export function clampFloorLevel(v) {
  const n = Number.isFinite(v) ? Math.round(v) : 0;
  return Math.min(FLOOR_LEVEL_MAX_CM, Math.max(FLOOR_LEVEL_MIN_CM, n));
}

// نوع‌های کدنویسی‌شده‌ی قدیمی → نزدیک‌ترین جنسِ واقعی (یا رنگِ ساده، وقتی معادلِ واقعی ندارد)
const LEGACY_FLOOR_TYPES = {
  parquet: 'parquetPbr',
  ceramic: 'ceramicPbr',
  stone: 'stonePbr',
  stoneSlab: 'stoneSlabPbr',
  stonePlain: 'paint',
  carpet: 'carpetPbr',
  concrete: 'concretePbr',
};

// rect.floorStyle ممکن است ناقص، خالی یا با نوعِ قدیمی باشد — همیشه یک شیءِ کامل و معتبر برمی‌گرداند؛
// رنگِ پیش‌فرض می‌تواند از بیرون (رنگِ همان اتاق از پالت) بیاید
export function resolveFloorStyle(rect, fallbackColor) {
  const s = rect?.floorStyle;
  const raw = LEGACY_FLOOR_TYPES[s?.type] || s?.type;
  const type = raw === 'paint' || PBR_FLOOR_TYPE_TO_SET[raw] ? raw : DEFAULT_FLOOR_STYLE.type;
  return {
    type,
    color: s?.color || fallbackColor || DEFAULT_FLOOR_STYLE.color,
    accent: s?.accent || DEFAULT_FLOOR_STYLE.accent,
    tileCm: s?.tileCm || DEFAULT_FLOOR_STYLE.tileCm,
    levelCm: clampFloorLevel(s?.levelCm ?? 0),
  };
}

// لبه‌ی عمودیِ کفِ بالاتر (همان «پیشانیِ پله») در یک ضلعِ اتاق — با همان جنس و رنگِ کف، و نقشه‌ی PBR با
// اندازه‌ی واقعیِ خودِ این نوار (نه کشیده‌شده). فقط جایی دیده می‌شود که دیوار نیست (درها/بازشوها)
function SlabEdge({ position, rotationY, length, height, set, unitCm, tintSet, color, typeKey }) {
  const repeatX = Math.max(0.05, length / unitCm);
  const repeatY = Math.max(0.02, height / unitCm);
  const maps = useMemo(
    () => loadPbrMaps(set, repeatX, repeatY),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [typeKey, repeatX, repeatY]
  );
  useEffect(() => () => disposePbrMaps(maps), [maps]);
  const tintProps = useTintProps(tintSet, maps, color);
  return (
    <mesh position={position} rotation={[0, rotationY, 0]}>
      <planeGeometry ref={ensureUv2} args={[length, height]} />
      <meshStandardMaterial
        key={typeKey}
        {...tintProps}
        map={maps?.map || null}
        normalMap={maps?.normalMap || null}
        roughnessMap={maps?.roughnessMap || null}
        aoMap={maps?.aoMap || null}
        roughness={1}
        metalness={0}
      />
    </mesh>
  );
}

// یک جعبه با «همان جنس و رنگِ کفِ یک اتاق» — برای پله/آستانه‌ی اختلافِ سطح، تا پله دقیقاً از جنسِ کفِ
// همان اتاق باشد (پارکت، سرامیک، سنگ، ...). style: خروجیِ resolveFloorStyle؛ layoutX/layoutY: ابعادِ رویه‌ی
// اصلیِ دیده‌شده (رویِ پله) برای اندازه‌ی واقعیِ بافت
export function FloorMaterialBox({ position, size, layoutX, layoutY, style }) {
  const s = style || DEFAULT_FLOOR_STYLE;
  const isPbr = Boolean(PBR_FLOOR_TYPE_TO_SET[s.type]);
  const set = isPbr ? PBR_SETS[PBR_FLOOR_TYPE_TO_SET[s.type]] : PBR_SETS[PAINT_SET_KEY];
  const unitCm = isPbr ? set.tilesPerImage * s.tileCm : set.baseSizeCm;
  const repeatX = Math.max(0.02, layoutX / unitCm);
  const repeatY = Math.max(0.02, layoutY / unitCm);
  const maps = useMemo(
    () => loadPbrMaps(set, repeatX, repeatY),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [s.type, repeatX, repeatY]
  );
  useEffect(() => () => disposePbrMaps(maps), [maps]);
  const tintProps = useTintProps(isPbr ? set : null, maps, s.color);
  return (
    <mesh position={position}>
      <boxGeometry ref={ensureUv2} args={size} />
      <meshStandardMaterial
        key={isPbr ? `pbr-${s.type}` : 'paint'}
        {...tintProps}
        map={maps?.map || null}
        normalMap={maps?.normalMap || null}
        roughnessMap={maps?.roughnessMap || null}
        aoMap={maps?.aoMap || null}
        roughness={1}
        metalness={0}
      />
    </mesh>
  );
}

// rect: {id, x1, y1, x2, y2, floorStyle?} — همان چیزی که createPlan.jsx در roomGroupRects می‌دهد.
// defaultColor: رنگِ پیش‌فرضِ همین اتاق از پالتِ چرخشی، وقتی کاربر چیزی تنظیم نکرده
// lightPct: «شدتِ روشناییِ اتاق» (پنلِ سقف) — فقط برای «رندرِ واقعی» روی سرور فرستاده می‌شود (نورِ سقفیِ اتاق)
// سایه‌ی کنج فقط کنارِ دیوارِ واقعی: لبه‌ی کف که روی «مرزِ فضا» است (بینِ دو قسمت، بدونِ دیوار) نباید نوارِ تیره بگیرد —
// وگرنه بعد از پوشش‌دادنِ کف، مرزِ قسمت‌ها یک خطِ سیاه روی زمین دیده می‌شود. لبه‌ی محوری به تکه‌های «روی دیوار»
// خرد می‌شود (ممکن است نصفش دیوار باشد و نصفش مرز)؛ لبه‌ی مورب با وسطش سنجیده می‌شود.
const nearWall = (x, z, walls, tol = 7) =>
  walls.some((w) => {
    const dx = w.x2 - w.x1;
    const dy = w.y2 - w.y1;
    const l2 = dx * dx + dy * dy || 1;
    const t = Math.max(0, Math.min(1, ((x - w.x1) * dx + (z - w.y1) * dy) / l2));
    return Math.hypot(x - (w.x1 + dx * t), z - (w.y1 + dy * t)) <= tol;
  });
export function wallEdgeRuns(edges, walls) {
  if (!walls) return edges;
  const out = [];
  edges.forEach((e) => {
    if (e.axis === 'd') {
      if (nearWall((e.x0 + e.x1) / 2, (e.z0 + e.z1) / 2, walls)) out.push(e);
      return;
    }
    const n = Math.max(2, Math.ceil((e.b - e.a) / 10));
    let start = null;
    for (let k = 0; k <= n; k += 1) {
      const t = e.a + ((e.b - e.a) * k) / n;
      const on = e.axis === 'x' ? nearWall(t, e.c, walls) : nearWall(e.c, t, walls);
      if (on && start == null) start = t;
      if ((!on || k === n) && start != null) {
        const end = on ? t : e.a + ((e.b - e.a) * (k - 1)) / n;
        if (end - start > 2)
          out.push({ ...e, a: start, b: end, convexA: start === e.a ? e.convexA : true, convexB: end === e.b ? e.convexB : true });
        start = null;
      }
    }
  });
  return out;
}
const rectEdges = (r) => {
  const x1 = Math.min(r.x1, r.x2);
  const x2 = Math.max(r.x1, r.x2);
  const z1 = Math.min(r.y1, r.y2);
  const z2 = Math.max(r.y1, r.y2);
  return [
    { axis: 'x', c: z1, a: x1, b: x2, n: 1, convexA: true, convexB: true },
    { axis: 'x', c: z2, a: x1, b: x2, n: -1, convexA: true, convexB: true },
    { axis: 'z', c: x1, a: z1, b: z2, n: 1, convexA: true, convexB: true },
    { axis: 'z', c: x2, a: z1, b: z2, n: -1, convexA: true, convexB: true },
  ];
};

// innerEdges: { z1, z2, x1, x2 } — لبه‌هایی از این تکه که به تکه‌ی دیگرِ همین فضا چسبیده‌اند (نه دیوار): نه سایه‌ی کنج،
// نه پیشانیِ پله، نه نوارِ انتخاب
// pieces: برای فضای چندتکه/مورب — همه‌ی تکه‌ها در «یک» کف با بافتِ پیوسته (spacePieces.jsx)
// walls: دیوارهای واقعی — سایه‌ی کنج فقط کنارِ همین‌ها (نه روی «مرزِ فضا»)
function Floor({ rect, defaultColor, isSelected, onSelect, lightPct = 70, innerEdges = null, pieces = null, walls = null }) {
  const frame = useMemo(() => (pieces ? piecesFrame(pieces) : rect), [pieces, rect]);
  const width = Math.max(1, frame.x2 - frame.x1);
  const depth = Math.max(1, frame.y2 - frame.y1);
  const centerX = (frame.x1 + frame.x2) / 2;
  const centerY = (frame.y1 + frame.y2) / 2;

  const s = resolveFloorStyle(rect, defaultColor);
  const level = s.levelCm;
  const isPbr = Boolean(PBR_FLOOR_TYPE_TO_SET[s.type]);
  const pbrSet = isPbr ? PBR_SETS[PBR_FLOOR_TYPE_TO_SET[s.type]] : null;
  // «رنگ» هم رنگِ واقعی است (بافتِ سطحِ رنگ‌شده، pbrTextures.PAINT_SET_KEY) با اندازه‌ی واقعیِ ثابت
  const paintSet = PBR_SETS[PAINT_SET_KEY];
  const repeatX = isPbr
    ? Math.max(0.25, width / (pbrSet.tilesPerImage * s.tileCm))
    : Math.max(0.25, width / paintSet.baseSizeCm);
  const repeatY = isPbr
    ? Math.max(0.25, depth / (pbrSet.tilesPerImage * s.tileCm))
    : Math.max(0.25, depth / paintSet.baseSizeCm);

  // عکسِ واقعیِ PBR: نقشه‌های رنگ/برجستگی/زبری/سایه؛ s.color روی خودِ عکس «ضرب» می‌شود. s.type هم در
  // وابستگی‌ها است چون سوییچ بینِ دو جنسِ PBR ممکن است repeat را عوض نکند ولی عکس باید عوض شود
  const pbrMaps = useMemo(() => {
    return loadPbrMaps(isPbr ? pbrSet : paintSet, repeatX, repeatY);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPbr, s.type, repeatX, repeatY]);

  useEffect(() => () => disposePbrMaps(pbrMaps), [pbrMaps]);

  // رنگ‌آمیزیِ هوشمند (کاشی): بندکشی رنگِ طبیعی‌اش را نگه می‌دارد، فقط خودِ کاشی رنگ می‌گیرد
  const tintProps = useTintProps(isPbr ? pbrSet : null, pbrMaps, s.color);

  const handleClick = (e) => {
    e.stopPropagation();
    if (onSelect) onSelect();
  };

  const multi = useMemo(() => {
    if (!pieces) return null;
    return {
      geometry: buildSurfaceGeometry(pieces, frame, level + 0.5, true),
      edges: spaceEdges(pieces),
    };
  }, [pieces, frame, level]);
  const aoEdges = useMemo(() => (multi ? wallEdgeRuns(multi.edges, walls) : walls ? wallEdgeRuns(rectEdges(rect), walls) : null), [multi, walls, rect]);
  useEffect(() => () => multi && multi.geometry.dispose(), [multi]);

  if (multi) {
    const aoW = Math.min(AO.floorWidth, Math.max(10, Math.min(width, depth) / 3));
    const typeKey = isPbr ? `pbr-${s.type}` : 'paint';
    return (
      <group>
        <mesh name="kh-floor" geometry={multi.geometry} userData={{ khFloor: true, roomId: rect.id, lightPct }} onClick={handleClick}>
          <meshStandardMaterial
            key={typeKey}
            {...tintProps}
            map={pbrMaps?.map || null}
            normalMap={pbrMaps?.normalMap || null}
            roughnessMap={pbrMaps?.roughnessMap || null}
            aoMap={pbrMaps?.aoMap || null}
            roughness={1}
            metalness={0}
          />
        </mesh>
        {multi.edges.map((e, i) => {
          const { nx, nz } = inward(e);
          const out = Math.atan2(-nx, -nz);
          const line = edgeBand(e, 0, 0.01, 0, 0.01);
          const ao = edgeBand(e, 0, aoW, level + 0.62, 0.01);
          const sel = edgeBand(e, -2, 2, level + 1.4, 1.5);
          return (
            <group key={i}>
              {level > 0 && line.len > 1 && (
                <SlabEdge
                  position={[line.position[0], (level + 0.5) / 2, line.position[2]]}
                  rotationY={out}
                  length={line.len}
                  height={level + 0.5}
                  set={isPbr ? pbrSet : paintSet}
                  unitCm={isPbr ? pbrSet.tilesPerImage * s.tileCm : paintSet.baseSizeCm}
                  tintSet={isPbr ? pbrSet : null}
                  color={s.color}
                  typeKey={typeKey}
                />
              )}
              {isSelected && sel.len > 1 && (
                <mesh position={sel.position} rotation={[0, sel.rotationY, 0]}>
                  <boxGeometry args={sel.size} />
                  <meshBasicMaterial color="#0a66c2" />
                </mesh>
              )}
            </group>
          );
        })}
        {aoEdges.map((e, i) => {
          const { nx, nz } = inward(e);
          const ao = edgeBand(e, 0, aoW, level + 0.62, 0.01);
          return ao.len > 1 ? (
            <AoStrip key={`ao${i}`} position={ao.position} rotation={[-Math.PI / 2, 0, Math.atan2(-nx, -nz)]} size={[ao.len, aoW]} opacity={AO.floorOpacity} />
          ) : null;
        })}
      </group>
    );
  }

  return (
    <group>
      <mesh
        name="kh-floor"
        userData={{ khFloor: true, roomId: rect.id, lightPct }}
        position={[centerX, level + 0.5, centerY]}
        rotation={[-Math.PI / 2, 0, 0]}
        onClick={handleClick}
      >
        <planeGeometry ref={ensureUv2} args={[width, depth]} />
        {/* یک متریالِ همیشه-یکسان با یک key که بینِ حالتِ PBR و بقیه‌ی انواع عوض می‌شود — نه یک
            ternary بینِ دو شکلِ مختلفِ props برای همین المان. تجربه‌ی قبلی (باگِ «سقفِ سیاه») نشان داد
            اگر همین المان با props ناقص/متفاوت به‌شرط رندر شود، three.js نمونه‌ی قبلیِ متریال را با
            propsِ کهنه دوباره‌استفاده می‌کند و نتیجه سیاه می‌شود؛ این‌جا همه‌ی props همیشه صراحتاً
            ست می‌شوند (مقدارشان فرق می‌کند، نه حضورشان) و key تعویضِ کامل را تضمین می‌کند */}
        <meshStandardMaterial
          key={isPbr ? `pbr-${s.type}` : 'paint'}
          {...tintProps}
          map={pbrMaps?.map || null}
          normalMap={pbrMaps?.normalMap || null}
          roughnessMap={pbrMaps?.roughnessMap || null}
          aoMap={pbrMaps?.aoMap || null}
          roughness={1}
          metalness={0}
        />
      </mesh>
      {/* کفِ بالاتر: چهار لبه‌ی عمودی (از سطحِ مبنا تا روی کف) — پیشانیِ پله در درها/بازشوها. کفِ پایین‌تر
          لبه لازم ندارد: دیوارها تا پایین‌ترین کف ادامه دارند و زیرِ درها/بازشوها یک «آستانه» پُرش می‌کند
          (Create3D.jsx) */}
      {level > 0 && (
        <group>
          {[
            { key: 'z1', pos: [centerX, (level + 0.5) / 2, rect.y1], rot: Math.PI, len: width },
            { key: 'z2', pos: [centerX, (level + 0.5) / 2, rect.y2], rot: 0, len: width },
            { key: 'x1', pos: [rect.x1, (level + 0.5) / 2, centerY], rot: -Math.PI / 2, len: depth },
            { key: 'x2', pos: [rect.x2, (level + 0.5) / 2, centerY], rot: Math.PI / 2, len: depth },
          ].filter((e) => !(innerEdges && innerEdges[e.key])).map((e) => (
            <SlabEdge
              key={e.key}
              position={e.pos}
              rotationY={e.rot}
              length={e.len}
              height={level + 0.5}
              set={isPbr ? pbrSet : paintSet}
              unitCm={isPbr ? pbrSet.tilesPerImage * s.tileCm : paintSet.baseSizeCm}
              tintSet={isPbr ? pbrSet : null}
              color={s.color}
              typeKey={isPbr ? `pbr-${s.type}` : 'paint'}
            />
          ))}
        </group>
      )}
      {/* سایه‌ی کنج روی کف، کنارِ دیوارها (ambientOcclusion.jsx) — برای حسِ عمق */}
      {aoEdges &&
        (() => {
          const s0 = Math.min(AO.floorWidth, width / 3, depth / 3);
          return s0 > 0
            ? aoEdges.map((e, i) => {
                const { nx, nz } = inward(e);
                const ao = edgeBand(e, 0, s0, level + 0.62, 0.01);
                return ao.len > 1 ? (
                  <AoStrip key={`ao${i}`} position={ao.position} rotation={[-Math.PI / 2, 0, Math.atan2(-nx, -nz)]} size={[ao.len, s0]} opacity={AO.floorOpacity} />
                ) : null;
              })
            : null;
        })()}
      {!aoEdges && <FloorEdgeAo
        x1={Math.min(rect.x1, rect.x2)}
        x2={Math.max(rect.x1, rect.x2)}
        z1={Math.min(rect.y1, rect.y2)}
        z2={Math.max(rect.y1, rect.y2)}
        y={level + 0.62}
        skip={innerEdges}
      />}
      {/* نوارِ باریکِ آبیِ دورتادورِ کف — نشانه‌ی بصریِ انتخاب، دقیقاً همان رنگِ نشانه‌ی انتخابِ در/
          پنجره/دیوار (#0a66c2)، فقط این‌جا چهار نوارِ جدا دورِ لبه‌ها به‌جای یک خط/جعبه‌ی تکی، چون کف
          یک مستطیلِ کامل است نه یک قابِ باریک */}
      {isSelected && (
        <group position={[0, level + 1.4, 0]}>
          {[
            { key: 'z1', pos: [centerX, 0, rect.y1], size: [width + 4, 1.5, 4] },
            { key: 'z2', pos: [centerX, 0, rect.y2], size: [width + 4, 1.5, 4] },
            { key: 'x1', pos: [rect.x1, 0, centerY], size: [4, 1.5, depth + 4] },
            { key: 'x2', pos: [rect.x2, 0, centerY], size: [4, 1.5, depth + 4] },
          ]
            .filter((e) => !(innerEdges && innerEdges[e.key]))
            .map((e) => (
              <mesh key={e.key} position={e.pos}>
                <boxGeometry args={e.size} />
                <meshBasicMaterial color="#0a66c2" />
              </mesh>
            ))}
        </group>
      )}
    </group>
  );
}

export default Floor;